import React, { useState } from "react";
import { List, EditButton, DeleteButton, ShowButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Button, Input, Card, Row, Col } from "antd";
import { SearchOutlined, AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { PROCUREMENT_PLAN_REPORT_LIST_QUERY, PROCUREMENT_PLAN_REPORT_DELETE_MUTATION } from "./queries";
import { IProcurementPlanReport } from "./interface";
import { useList } from "@refinedev/core";
import { TEAM_LIST_QUERY } from "../teams/queries";
import { UserInfo } from "../../components/UserInfo";

// Status badges for procurement plan reports
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusColors: Record<string, string> = {
    DRAFT: 'default',
    SUBMITTED: 'processing',
    APPROVED: 'success',
    REJECTED: 'error'
  };

  const statusText: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected'
  };

  return (
    <Tag color={statusColors[status] || 'default'}>
      {statusText[status] || status}
    </Tag>
  );
};

export const ProcurementPlanReportList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Fetch teams for mapping teamId to team name
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  
  const teams = Array.isArray(teamsData?.data) ? teamsData?.data : (teamsData?.data as any)?.teams?.nodes || [];

  // Fetch all procurement plan reports without filtering (API doesn't support server-side filtering)
  const { data: reportsData, isLoading: reportsLoading, isError: reportsError } = useList<any>({
    resource: "procurementPlanReports",
    meta: {
      gqlQuery: PROCUREMENT_PLAN_REPORT_LIST_QUERY,
    },
    pagination: { mode: "off" },
    filters: [],
    sorters: []
  });

  // Get the raw data and apply client-side filtering
  const allReports = Array.isArray(reportsData?.data) ? reportsData.data : (reportsData?.data as any)?.procurementPlanReports?.nodes || [];
  
  // Apply client-side filtering
  const filteredReports = allReports.filter((report: any) => {
    const matchesYear = !yearFilter || report.year === yearFilter;
    const matchesStatus = !statusFilter || report.status === statusFilter;
    const matchesSearch = !searchTerm || 
      report.year.toString().includes(searchTerm) || 
      report.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.createdBy?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesYear && matchesStatus && matchesSearch;
  });

  // Mock tableProps for RefineJS compatibility
  const tableProps = {
    dataSource: filteredReports,
    loading: reportsLoading,
    pagination: {
      total: filteredReports.length,
      pageSize: 10,
      showSizeChanger: false,
      showQuickJumper: false,
      showTotal: (total: number) => `Total ${total} items`,
    },
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleYearFilter = (value: string) => {
    const year = parseInt(value);
    setYearFilter(isNaN(year) ? null : year);
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value || null);
  };

  const dataSource: IProcurementPlanReport[] = filteredReports;

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Button.Group>
              <Button
                icon={<UnorderedListOutlined />}
                type={viewMode === 'list' ? 'primary' : 'default'}
                onClick={() => setViewMode('list')}
              />
              <Button
                icon={<AppstoreOutlined />}
                type={viewMode === 'grid' ? 'primary' : 'default'}
                onClick={() => setViewMode('grid')}
              />
            </Button.Group>

            <Input
              placeholder="Filter by year..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleYearFilter(e.target.value)}
              onPressEnter={(e) => handleYearFilter(e.currentTarget.value)}
              style={{ width: 150 }}
            />

            <Input
              placeholder="Filter by status..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleStatusFilter(e.target.value)}
              onPressEnter={(e) => handleStatusFilter(e.currentTarget.value)}
              style={{ width: 150 }}
            />

            <CreateButton />
          </Space>
        ),
      }}
    >
      {viewMode === 'list' ? (
        <Table {...tableProps} rowKey="id">
          <Table.Column
            dataIndex="year"
            title="Year"
            sorter
          />
          <Table.Column
            dataIndex="status"
            title="Status"
            render={(status: string) => <StatusBadge status={status} />}
            sorter
          />
          <Table.Column
            dataIndex="createdBy"
            title="Created By"
            sorter
            render={(userId: string) => userId ? <UserInfo userId={userId} /> : '-'}
          />
          <Table.Column
            dataIndex="totalAmount"
            title="Total Amount"
            sorter
            render={(value: number) => `ETB ${value.toLocaleString()}`}
          />
          <Table.Column
            dataIndex="plans"
            title="Plans Count"
            render={(plans: any[]) => plans?.length || 0}
          />
          <Table.Column
            dataIndex="submissionDate"
            title="Submission Date"
            sorter
            render={(date: string) => date ? new Date(date).toLocaleDateString() : '-'}
          />
          <Table.Column
            dataIndex="isOutdated"
            title="Outdated"
            render={(isOutdated: boolean) => (
              isOutdated ? <Tag color="warning">Yes</Tag> : <Tag color="success">No</Tag>
            )}
          />
          <Table.Column
            title="Actions"
            dataIndex="actions"
            render={(_text: string, record: IProcurementPlanReport) => (
              <Space>
                {record.status === 'DRAFT' && (
                  <>
                    <EditButton size="small" recordItemId={record.id} />
                    <DeleteButton size="small" recordItemId={record.id} meta={{ gqlMutation: PROCUREMENT_PLAN_REPORT_DELETE_MUTATION }} />
                  </>
                )}
                <ShowButton size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        </Table>
      ) : (
        <Row gutter={[16, 16]}>
          {dataSource.map((item) => {
            return (
              <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  size="small"
                  title={`Report for ${item.year}`}
                  extra={<StatusBadge status={item.status} />}
                >
                  <div style={{ marginBottom: 8 }}>
                    <strong>Created By: </strong>{item.createdBy ? <UserInfo userId={item.createdBy} /> : '-'}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Total Amount: </strong>ETB {item.totalAmount?.toLocaleString?.() ?? item.totalAmount}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Plans Count: </strong>{item.plans?.length || 0}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Submission Date: </strong>{item.submissionDate ? new Date(item.submissionDate).toLocaleDateString() : '-'}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Outdated: </strong>{item.isOutdated ? 'Yes' : 'No'}
                  </div>
                  <Space>
                    {item.status === 'DRAFT' && (
                      <>
                        <EditButton size="small" recordItemId={item.id} />
                        <DeleteButton size="small" recordItemId={item.id} meta={{ gqlMutation: PROCUREMENT_PLAN_REPORT_DELETE_MUTATION }} />
                      </>
                    )}
                    <ShowButton size="small" recordItemId={item.id} />
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </List>
  );
};